import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { GearIcon, useSettingsDrawer } from '@cca/ui';
import { AuthLayout } from '../auth/AuthLayout';
import { VettingSettings } from './VettingSettings';
import { IdentityReviewSection } from './IdentityReviewSection';
import { RelinkControl } from './RelinkControl';
import { RELINK, type RelinkOutcomeKind } from './identity-review';
import { apiFetch, apiPost } from './api';
import { driveMoveNote, type DriveMoveDetail, type DriveMoveNote } from './drive-move-note';
import { NzpvsCopyButton } from './nzpvs-filler/NzpvsCopyButton';
import {
  RETRY_LIST_HEADING,
  STUCK_LIST_HEADING,
  chunkPackPages,
  downloadedLabel,
  isPackPageSize,
  lodgeConfirmText,
  lodgeResultMessage,
  markDisabled,
  packDownloadedAnnouncement,
  packErrorsAfter,
  packFailureView,
  packFilenameFrom,
  packHref,
  packMarkConfirmOptions,
  packNetworkFailure,
  packPageLabel,
  packReadiness,
  pageMarkLabel,
  recordDownloads,
  restoreDownloads,
  rowLodgeConfirmText,
  rowConsentWarning,
  rowMarkDisabled,
  rowNotesText,
  type DownloadOutcome,
  type DownloadRecord,
  type DownloadStore,
  type PackMarkControl,
  type LodgeAllResult,
  type PackFailureLine,
  type PackFailureView,
  type PackPage,
} from './lodgement-card';

// ── Types ──────────────────────────────────────────────────────────────────

type VettingStatus =
  | 'Submitted by Applicant'
  | 'Parish Approved Pending Lodging'
  | 'Submitted to NZPV'
  | 'Result Received'
  | 'Expired'
  | 'Cancelled';

interface StatsResponse {
  counts: Record<VettingStatus, number>;
  quarantineCount: number;
}

interface ParishItem {
  id: string;
  name: string;
  slug: string;
}

interface LodgementItem {
  id: string;
  applicantName: string;
  parishName: string;
  jobTitle: string;
  approvedAt: string | null;
  pdfFileId: string | null;
  /** Would the NZPVS portal accept this row as-is? (server-assessed, cca-omhx) */
  lodgeable: boolean;
  /** Police column names that are blank — empty when lodgeable. */
  missing: string[];
  /** The same columns in the words on the application form (what to fix). */
  missingLabels: string[];
  /** Things to know before lodging that do not block it (cca-49qxu). */
  notes?: string[];
  /** The one of `notes` drawn as a warning: the consent is too old for NZPVS (cca-uzt5). */
  consentWarning?: string | null;
}

/**
 * The two consent columns are not editor-repairable — only the applicant's
 * signature fills them. The note's call-to-action branches on this.
 */
const CONSENT_COLUMNS = ['Signed Consent Form', 'Date Consent Signed'];

/**
 * The lodgement queue's warning pill: the "Not lodgeable" note, and the
 * too-old consent (Miranda M-S2). Warn tokens are themed (global.css
 * --amber/--amber-lt).
 */
const WARN_PILL: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 12,
  color: 'var(--color-warn, #854f0b)',
  background: 'var(--color-warn-wash, #fff8e1)',
  border: '1px solid var(--color-warn, #854f0b)',
  borderRadius: 4,
  padding: '3px 8px',
};

/** A link inside a warning pill: the pill's own ink, underlined and bold. */
const WARN_PILL_LINK: React.CSSProperties = {
  color: 'inherit',
  fontWeight: 600,
  textDecoration: 'underline',
};

interface AwaitingResultItem {
  id: string;
  applicantName: string;
  parishName: string;
  jobTitle: string;
  nzpvRequestId: string | null;
  lodgedAt: string | null;
  pdfFileId: string | null;
}

interface ResultFlag {
  recordId: string;
  flagType: string;
  reason: string | null;
  applicantName: string;
  nzpvApplicationId: string;
  createdAt: string;
}

// Identity review's own types, fetch helpers and pure decisions live beside it
// (./identity-review.ts, ./IdentityReviewSection.tsx); `apiFetch`/`apiPost` are
// now shared from ./api so a 409 body reaches the code that must act on it.

// ── Shared UI ──────────────────────────────────────────────────────────────

/** The page that mints the application's edit link (corrections write back to
 *  Zoho + the supplement overlay — including the lodgement-only fields). */
function editLinkHref(id: string): string {
  return `/api/vetting/application/${id}/edit-link`;
}

/** Opens the printable application view (the prefilled NZPV consent form) + an
 *  Edit link (corrections write back to Zoho + the overlay). */
function ViewAppLink({ id, describedBy }: { id: string; describedBy?: string | undefined }) {
  const style = {
    fontSize: 12,
    fontWeight: 600,
    color: '#1e4d3f',
    textDecoration: 'none',
  } as const;
  return (
    <span style={{ display: 'inline-flex', gap: 14, flexWrap: 'wrap' }}>
      <a
        href={`/api/vetting/application/${id}`}
        target="_blank"
        rel="noopener"
        style={style}
        aria-describedby={describedBy}
      >
        View / print application ↗
      </a>
      <a
        href={editLinkHref(id)}
        target="_blank"
        rel="noopener"
        style={style}
        aria-describedby={describedBy}
      >
        Edit ↗
      </a>
    </span>
  );
}

/** Saved Drive PDF when available (post-approval), else the live view.
 *  `describedBy` ties the link to its row's notes (cca-49qxu, Miranda r1 M-11). */
function DocLink({
  id,
  pdfFileId,
  describedBy,
}: {
  id: string;
  pdfFileId: string | null;
  /** Explicitly `| undefined` (exactOptionalPropertyTypes): a row with no notes passes undefined. */
  describedBy?: string | undefined;
}) {
  if (!pdfFileId) return <ViewAppLink id={id} describedBy={describedBy} />;
  return (
    <a
      href={`https://drive.google.com/file/d/${pdfFileId}/view`}
      target="_blank"
      rel="noopener"
      style={{ fontSize: 12, fontWeight: 600, color: '#1e4d3f', textDecoration: 'none' }}
      aria-describedby={describedBy}
    >
      Open saved application (Drive) ↗
    </a>
  );
}

const CARD: React.CSSProperties = {
  background: 'white',
  border: '1px solid #e0e0d8',
  borderRadius: 8,
  padding: '20px 24px',
  marginBottom: 12,
};

/**
 * `headingRef`, where a section passes one, makes the h2 a focus target for
 * code only (`tabIndex={-1}`: never a tab stop) — where focus goes after a
 * successful "Try again" (useHeadingFocus). Its ring is
 * `.section-head-focus-target:focus-visible` in this portal's global.css (the
 * estate ring, as on .idr-confirm-head): vetting never loads @cca/ui's
 * base.css, so no estate-wide rule reaches the h2. The class goes on only with
 * `headingRef`, so headings that never take focus carry none.
 */
function SectionHead({
  title,
  count,
  headingRef,
}: {
  title: string;
  count: number | string;
  headingRef?: React.Ref<HTMLHeadingElement>;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        marginBottom: 16,
        paddingBottom: 10,
        borderBottom: '2px solid #1e4d3f',
      }}
    >
      <span
        style={{
          background: '#1e4d3f',
          color: 'white',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.05em',
          padding: '3px 8px',
          borderRadius: 3,
        }}
      >
        {count}
      </span>
      {/* A real heading, for the same reason as the parish page's
          CollapsibleHead: these are the dashboard's sections — "Results needing
          attention" and "Parish dashboards" — and as a styled <span> they were
          absent from the document outline entirely. */}
      <h2
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
        className={headingRef ? 'section-head-focus-target' : undefined}
        style={{ fontSize: 16, fontWeight: 600, margin: 0 }}
      >
        {title}
      </h2>
    </div>
  );
}

/** SectionHead whose count pill is the click target to show/hide the body.
 *  `headingRef` (and the focus-ring class it brings) as SectionHead's. */
function CollapsibleHead({
  title,
  count,
  open,
  onToggle,
  right,
  headingRef,
}: {
  title: string;
  count: number | string;
  open: boolean;
  onToggle: () => void;
  right?: React.ReactNode;
  headingRef?: React.Ref<HTMLHeadingElement>;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        paddingBottom: 10,
        borderBottom: '2px solid #1e4d3f',
        flexWrap: 'wrap',
      }}
    >
      {/* A disclosure, not a decorative number: a screen-reader user used to
          hear only "12, button" (WCAG 4.1.2). The count stays the click target,
          but it now says what it toggles and whether it is open. */}
      <button
        type="button"
        onClick={onToggle}
        className="count-toggle"
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} ${title} (${count})`}
        title={open ? 'Hide' : 'Show'}
        style={{
          background: '#1e4d3f',
          color: 'white',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.05em',
          padding: '3px 8px',
          borderRadius: 3,
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {count}
      </button>
      {/* A real heading, the same swap the parish page's CollapsibleHead
          already carries. These are the registrar dashboard's sections —
          "Ready to lodge with NZPV", "Awaiting result from NZPV", "Expired
          results" — and as a styled <span> they were absent from the document
          outline, so the page went h1 → (nothing) → the item-level h3s. */}
      <h2
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
        className={headingRef ? 'section-head-focus-target' : undefined}
        style={{ fontSize: 16, fontWeight: 600, margin: 0 }}
      >
        {title}
      </h2>
      {open && right && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          {right}
        </div>
      )}
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e0e0d8',
        borderRadius: 8,
        padding: '32px 24px',
        textAlign: 'center',
        color: '#666',
        fontSize: 14,
      }}
    >
      {msg}
    </div>
  );
}

/**
 * A section whose list could not be fetched (UX-7, ux.md:194): one error line
 * and a way to try again. Never `Empty` beside it, and never a 0 in the head —
 * the head shows "…", the mark the Expired and Reconcile heads already show
 * while their count is not known. "No applications awaiting a result." under a
 * "0" read as "nothing is waiting" when nobody knew (cca-zbmdy) — the
 * passkey-list incident UX-7 was written for. The shape is the identity
 * review's own (IdentityReviewSection:
 * `role="alert"` on the line, a ghost "Try again" under it), in this page's
 * error ink. A retry that fails again leaves the line as it was, so it is
 * announced once, not again on every press.
 *
 * The button stays enabled while a retry is out: disabling the focused button
 * would drop focus as surely as unmounting it. Overlapping presses are made
 * safe by useLatestRequest instead.
 */
function LoadFailure({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <p role="alert" style={{ color: 'var(--red)', fontSize: 13, margin: '0 0 8px' }}>
        {message}
      </p>
      {/* Called with no arguments: the click event is not a load option. */}
      <button type="button" className="btn btn-ghost" onClick={() => onRetry()}>
        Try again
      </button>
    </div>
  );
}

/**
 * Where focus goes after a successful "Try again" (WCAG 2.4.3, cca-zbmdy r1):
 * the button that held it unmounts with the error, and focus fell to <body>.
 * The section's heading takes it instead — pass `headingRef` to the section's
 * head, call `focusHeading()` when a RETRY succeeds (never on a first load).
 * Applied in an effect, after the commit that removes the button, as the
 * lodgement card's `focusAfter` is.
 */
function useHeadingFocus() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!pending) return;
    headingRef.current?.focus();
    setPending(false);
  }, [pending]);
  return { headingRef, focusHeading: () => setPending(true) };
}

/**
 * Overlapping loads — two presses of "Try again" while the first is still out
 * (cca-zbmdy r1). Answers can land in either order, and a slow failure landing
 * last painted its error over a list the later press had loaded. Each load
 * takes a ticket (`const isLatest = nextRequest()`); only while `isLatest()`
 * may its answer be applied.
 */
function useLatestRequest() {
  const seq = useRef(0);
  return () => {
    const mine = ++seq.current;
    return () => mine === seq.current;
  };
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Stats bar ─────────────────────────────────────────────────────────────

// Pills with an `anchor` scroll to that section; the rest (Submitted = sitting
// with parishes; Cancelled = terminal) have no registrar section and stay inert.
const STAT_CONFIG: { status: VettingStatus; label: string; accent: string; anchor?: string }[] = [
  { status: 'Submitted by Applicant', label: 'Submitted', accent: '#2563eb' },
  {
    status: 'Parish Approved Pending Lodging',
    label: 'Pending lodging',
    accent: '#d97706',
    anchor: 'sec-lodgement',
  },
  { status: 'Submitted to NZPV', label: 'With NZPV', accent: '#7c3aed', anchor: 'sec-awaiting' },
  { status: 'Result Received', label: 'Results in', accent: '#16a34a', anchor: 'sec-results' },
  { status: 'Expired', label: 'Expired', accent: '#dc2626', anchor: 'sec-expired' },
  { status: 'Cancelled', label: 'Cancelled', accent: '#6b7280' },
];

/** True when the reader has asked for less motion (UX-5). The CSS block in
 *  global.css neutralises the flash animation; the smooth scroll is a JS
 *  option, so it has to be gated here as well. */
function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Scroll to a section and briefly flash it so you see where you landed —
 *  neither the scroll nor the flash happens under reduced motion. */
function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduced = prefersReducedMotion();
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  if (reduced) return;
  el.classList.add('section-flash');
  window.setTimeout(() => el.classList.remove('section-flash'), 1300);
}

function Pill({
  count,
  label,
  accent,
  anchor,
}: {
  count: number;
  label: string;
  accent: string;
  anchor?: string;
}) {
  const active = count > 0;
  const base: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '5px 12px',
    borderRadius: 20,
    background: active ? `${accent}14` : '#f5f5f3',
    border: `1px solid ${active ? accent + '40' : '#e0e0d8'}`,
    font: 'inherit',
  };
  const inner = (
    <>
      {/* An inactive pill used to be #aaa on #f5f5f3 — 2.13:1, well under the
          SC 1.4.3 floor. The muted role token is AA on both grounds. */}
      <span
        style={{
          fontWeight: 700,
          fontSize: 14,
          color: active ? accent : 'var(--color-muted)',
          minWidth: 12,
          textAlign: 'right',
        }}
      >
        {count}
      </span>
      <span style={{ fontSize: 12, color: active ? 'var(--color-ink)' : 'var(--color-muted)' }}>
        {label}
      </span>
    </>
  );
  if (!anchor) return <div style={base}>{inner}</div>;
  return (
    <button
      type="button"
      onClick={() => scrollToSection(anchor)}
      title={`Jump to ${label}`}
      style={{ ...base, cursor: 'pointer' }}
    >
      {inner}
    </button>
  );
}

/** `refreshKey` changes whenever an action elsewhere on the page moved a count
 *  (an identity review resolved, an application lodged) — the pills used to be
 *  fetched once and then quietly went stale. */
function StatsBar({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<StatsResponse | null>(null);

  useEffect(() => {
    apiFetch<StatsResponse>('/api/vetting/registrar/stats')
      .then(setData)
      .catch(() => {
        /* non-fatal — stats bar just stays hidden */
      });
  }, [refreshKey]);

  if (!data) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 28,
        padding: '14px 18px',
        background: 'white',
        border: '1px solid #e0e0d8',
        borderRadius: 8,
      }}
    >
      {STAT_CONFIG.map((s) => (
        <Pill
          key={s.status}
          count={data.counts[s.status] ?? 0}
          label={s.label}
          accent={s.accent}
          {...(s.anchor ? { anchor: s.anchor } : {})}
        />
      ))}
      <Pill
        count={data.quarantineCount ?? 0}
        label="Identity review"
        accent="#d97706"
        anchor="sec-identity"
      />
    </div>
  );
}

// ── Parish form links ──────────────────────────────────────────────────────

function ParishLinksSection() {
  const navigate = useNavigate();
  const [parishes, setParishes] = useState<ParishItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiFetch<{ parishes: ParishItem[] }>('/api/vetting/registrar/parishes')
      .then((d) => setParishes(d.parishes))
      .catch(() => {
        /* non-fatal */
      })
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || parishes.length === 0) return null;

  return (
    <div style={{ marginBottom: 40 }}>
      <SectionHead title="Parish dashboards" count={parishes.length} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <label
          htmlFor="registrar-parish-dashboard-select"
          style={{ fontSize: 13, fontWeight: 600, color: '#444' }}
        >
          View a parish dashboard:
        </label>
        <select
          id="registrar-parish-dashboard-select"
          className="input"
          style={{ maxWidth: 320 }}
          value=""
          onChange={(e) => {
            if (e.target.value) void navigate(`/parish/${e.target.value}`);
          }}
        >
          <option value="">Select a parish…</option>
          {parishes.map((p) => (
            <option key={p.id} value={p.slug}>
              {p.name}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: '#888' }}>
          Opens the exact view the parish approver sees — form links and invitations live there.
        </span>
      </div>
    </div>
  );
}

// ── Section 1: Lodgement queue ─────────────────────────────────────────────

/** Hand a downloaded blob to the browser's download manager. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked after the click has been handed off, not synchronously.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * The download record lives in this browser's localStorage. Reached through a
 * getter so an origin that forbids storage throws inside lodgement-card's
 * try/catch, not here — the card then carries on with the in-session record.
 */
const browserStore = (): DownloadStore => window.localStorage;

/**
 * The card's state that must outlive the `refreshKey` remount. Held by the
 * PAGE (cca-omhx I4): the remount that reloads every section after a mark used
 * to throw this card's error state away in the same tick it was set, so
 * "N lodged; failed: X" was never seen. The download record is here for the
 * same reason — with storage unavailable, a remount would otherwise forget
 * every pack downloaded this session.
 */
interface LodgementMessages {
  error: string | null;
  setError: (message: string | null) => void;
  /** Per pack, keyed by PackPage.key (the pack's ids). */
  packErrors: Record<string, PackFailureView>;
  /** Moves the error map through lodgement-card's packErrorsAfter — the one rule. */
  applyDownloadOutcome: (key: string, outcome: DownloadOutcome<PackFailureView>) => void;
  /** Applicant id → when a pack holding them was downloaded in this browser. */
  downloads: DownloadRecord;
  setDownloads: React.Dispatch<React.SetStateAction<DownloadRecord>>;
}

/**
 * What a row says once the server has proved the list on screen is out of date
 * (Miranda S-1). Shown BESIDE the disabled mark, not only in its `title`: a
 * disabled control that gives no reason is the hazard the move's own refusal
 * copy exists to avoid, and the row can be collapsed away from the refusal
 * sentence that caused this.
 *
 * It names the act the registrar can actually perform (UX-7, ux.md:99; UX-6,
 * ux.md:87). "Reload the queue" named a control this dashboard does not have —
 * there is no refresh affordance on this screen — so it sent him looking for a
 * button that isn't there. Reloading the page is what he has.
 */
const STALE_ROW_NOTE = 'This row is out of date — reload the page to see it as it is now.';

function LodgementSection({
  onRefresh,
  messages,
}: {
  onRefresh: () => void;
  messages: LodgementMessages;
}) {
  const { error, setError, packErrors, applyDownloadOutcome, downloads, setDownloads } = messages;
  const [items, setItems] = useState<LodgementItem[]>([]);
  /** As published by the queue route — the only authority for the page size. */
  const [packPageSize, setPackPageSize] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);
  /**
   * The queue could not be fetched (cca-zbmdy, UX-7). Local, NOT the hoisted
   * `error`: a remount reloads the queue, so a load failure belongs to the
   * instance that saw it. In `error` it also outlived the next good load —
   * nothing cleared it — and sat above a list that had loaded fine.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const nextRequest = useLatestRequest();
  const { headingRef, focusHeading } = useHeadingFocus();
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * The move's own live region, and it stays HERE rather than joining `error`
   * in the hoisted `messages`: a move's sentence belongs to this section and
   * must survive the keyed remount that `error` is hoisted to escape. (`error`
   * and `setError` are NOT re-declared — cca-omhx lifted them into `messages`
   * above, and a second copy would shadow the one the packs write to.)
   */
  const [notice, setNotice] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  /**
   * The ONE compact row expanded into its full card, or null. One at a time:
   * the expanded card is tall (it carries the document link, the mark and the
   * move), and a glut of them open at once would bury the pack controls that
   * are the point of the paged view.
   */
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  /**
   * The ONE row whose "Copy for NZPVS" copy is on the clipboard now, or null
   * (cca-uzt5, Miranda M-S1): the clipboard holds one copy, so the page says
   * "Copied" on one row only. It moves when a row's write SUCCEEDS; every row
   * that copied before it then reads "Copied earlier — no longer on the
   * clipboard". Held here, not in each control, so the rows cannot disagree.
   */
  const [clipboardRow, setClipboardRow] = useState<string | null>(null);
  /**
   * Rows the server has refused a move on as `already_lodged` or `not_found`
   * (Miranda S-1). This queue lists only `Parish Approved Pending Lodging`, so
   * either refusal says the list on screen is out of date and this row's own
   * "Mark lodged" would be refused as well — it comes off, with the reason
   * beside it, rather than standing enabled under a sentence that says the
   * application is already lodged.
   */
  const [staleRows, setStaleRows] = useState<ReadonlySet<string>>(() => new Set());
  /** What a pack's status region says after its download is saved (SC 4.1.3). */
  const [announcement, setAnnouncement] = useState<{ key: string; text: string } | null>(null);
  /**
   * Where focus goes once a download's outcome has rendered (Miranda S2, WCAG
   * 2.2 SC 2.4.3): the pack's Mark button after a save, the alert's "Try
   * download again" after a refusal — or the pack's heading when that button
   * is not mounted or not enabled. Applied in an effect, after the commit
   * that mounts the target.
   */
  const [focusAfter, setFocusAfter] = useState<{ key: string; target: 'mark' | 'retry' } | null>(
    null,
  );
  const markButtons = useRef(new Map<string, HTMLButtonElement>());
  const retryButtons = useRef(new Map<string, HTMLButtonElement>());
  const packHeadings = useRef(new Map<string, HTMLHeadingElement>());

  /** `retry`: started by "Try again", so a success focuses the heading. Every
   *  caller's load takes a ticket — only the latest one's answer is applied
   *  (useLatestRequest). */
  async function load(retry = false) {
    const isLatest = nextRequest();
    try {
      const d = await apiFetch<{ queue: LodgementItem[]; packPageSize?: unknown }>(
        '/api/vetting/registrar/lodgement-queue',
      );
      if (!isLatest()) return;
      setItems(d.queue);
      setPackPageSize(d.packPageSize);
      setLoadError(null);
      // Stored downloads merged with this session's, pruned to the queue and
      // the fortnight (lodgement-card.ts restoreDownloads).
      const queueIds = d.queue.map((i) => i.id);
      setDownloads((prev) =>
        restoreDownloads({ store: browserStore, memory: prev, queueIds, now: new Date() }),
      );
      if (retry) focusHeading();
    } catch {
      if (!isLatest()) return;
      setLoadError('Could not load the applications ready to lodge.');
    }
    setLoaded(true);
  }

  const nameOf = (id: string) => items.find((i) => i.id === id)?.applicantName ?? id;

  /**
   * Fetch one pack and save it. Fetched by a <button>, never an <a href>, so a
   * middle-click or "open in new tab" cannot fetch the pack around this
   * handler and its record (Lazo L1); and so a refusal (the worker never ships
   * a partial pack) is shown in place rather than replacing the dashboard with
   * a JSON body. Only once the blob is saved are the pack's applicants
   * recorded as downloaded — and only then is a standing refusal cleared,
   * never at the start of a retry (LeC). The error map moves only through
   * packErrorsAfter (lodgement-card.ts), which holds that rule.
   */
  async function handleDownload(page: PackPage<LodgementItem>) {
    const packNumber = page.of > 1 ? page.number : null;
    setDownloading(page.key);
    setAnnouncement(null);
    applyDownloadOutcome(page.key, { kind: 'started' });
    try {
      const res = await fetch(packHref(page.ids), { credentials: 'include' });
      if (res.ok && (res.headers.get('Content-Type') ?? '').startsWith('application/zip')) {
        saveBlob(await res.blob(), packFilenameFrom(res.headers.get('Content-Disposition')));
        const at = new Date();
        setDownloads((prev) =>
          recordDownloads({ store: browserStore, memory: prev, ids: page.ids, at }),
        );
        applyDownloadOutcome(page.key, { kind: 'saved' });
        setAnnouncement({ key: page.key, text: packDownloadedAnnouncement(page.number) });
        setFocusAfter({ key: page.key, target: 'mark' });
        return;
      }
      const body: unknown = await res.json().catch(() => null);
      applyDownloadOutcome(page.key, {
        kind: 'refused',
        failure: packFailureView(res.status, body, nameOf, packNumber),
      });
      setFocusAfter({ key: page.key, target: 'retry' });
    } catch {
      applyDownloadOutcome(page.key, { kind: 'refused', failure: packNetworkFailure(packNumber) });
      setFocusAfter({ key: page.key, target: 'retry' });
    } finally {
      setDownloading(null);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!focusAfter) return;
    const { key, target } = focusAfter;
    const button = (target === 'mark' ? markButtons : retryButtons).current.get(key);
    if (button && !button.disabled) button.focus();
    else packHeadings.current.get(key)?.focus();
    setFocusAfter(null);
  }, [focusAfter]);

  /** A callback ref that files an element under its pack's key while it is mounted. */
  const fileUnder =
    <E extends HTMLElement>(map: Map<string, E>, key: string) =>
    (el: E | null) => {
      if (el) map.set(key, el);
      else map.delete(key);
    };

  async function handleLodge(item: LodgementItem) {
    // Confirmed, like a pack's mark (Miranda S1): from this page a lodge is
    // not undone, so the one named person is asked about before any write.
    if (!window.confirm(rowLodgeConfirmText(item))) return;
    const id = item.id;
    setBusy(id);
    setError(null);
    // The shared status region still holds the LAST move's sentence ("Moved
    // Ann's application to …"). Lodging is a different act on a different row,
    // and leaving that sentence standing beside it makes a stale success look
    // like this press's result. `onAttempt` clears it for a move; this is the
    // same clear for the primary action.
    setNotice('');
    try {
      // The NZPV application number is captured automatically when the
      // result email arrives.
      await apiPost('/api/vetting/registrar/record/' + id + '/lodge', {});
      await load();
      onRefresh();
    } catch (e: unknown) {
      const err = e as {
        status?: number;
        data?: { error?: string; current?: string; missingLabels?: string[] };
      };
      // "Already lodged" — a duplicate/second click that landed after the first
      // succeeded (status moved past 'Parish Approved Pending Lodging'). That's
      // not a failure: just refresh so the row drops into its new section.
      if (err?.status === 409 && err.data?.error === 'invalid_status') {
        await load();
        onRefresh();
        return;
      }
      // The server refuses a row the export would not carry (cca-djp1). The
      // queue is reloaded so the row's missing-fields note is current — the
      // button is disabled for exactly these rows, so this is the belt to the
      // card's braces, not the everyday path.
      if (err?.status === 409 && err.data?.error === 'not_lodgeable') {
        setError(
          `Not lodged — this application is missing ${
            err.data.missingLabels?.join(', ') ?? 'required fields'
          }. It was never sent to Police; fix it below, then mark it lodged.`,
        );
        await load();
        return;
      }
      setError(err?.data?.error ? `Lodge failed: ${err.data.error}` : 'Lodge failed.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Mark exactly `marking` lodged. The ids sent are the ones the adjacent
   * download carries — a pack page's `ids`, or the single pack's — so the
   * server acts on the set this card displayed, and says so (409) if the
   * queue moved underneath it.
   */
  async function handleLodgeSet(busyKey: string, marking: LodgementItem[], confirmText: string) {
    if (marking.length === 0) return;
    if (!window.confirm(confirmText)) return;
    setBusy(busyKey);
    setError(null);
    setNotice(''); // same stale-sentence clear as handleLodge above

    try {
      // `skipped` comes back empty on this path by construction: naming the ids
      // makes a non-lodgeable row a 409, never a quiet omission.
      const res = await apiPost<LodgeAllResult>(
        '/api/vetting/registrar/lodgement-queue/lodge-all',
        { ids: marking.map((i) => i.id) },
      );
      setError(lodgeResultMessage(res));
      await load();
      onRefresh();
    } catch (e: unknown) {
      const err = e as {
        status?: number;
        data?: { error?: string; refused?: { name: string }[]; unknown?: string[] };
      };
      if (err.status === 409 && err.data?.error === 'not_lodgeable') {
        const names = (err.data.refused ?? []).map((r) => r.name).join(', ');
        setError(
          `Nothing was lodged — the queue changed while this page was open${
            names ? ` (${names} can no longer be lodged)` : ''
          }. It has been reloaded; check the list and try again.`,
        );
        // No onRefresh(): nothing moved, so the other sections are already
        // right. (The message itself now survives a remount — it lives on the
        // page — so this is about not reloading for nothing.)
        await load();
        return;
      }
      setError(e instanceof Error ? e.message : 'Lodge all failed.');
    } finally {
      setBusy(null);
    }
  }

  if (!loaded) return <p style={{ color: '#666', fontSize: 14 }}>Loading…</p>;

  // Packs are derived ONCE here, from the queue order and the page size the
  // worker published (cca-omhx I1/I2): a pack's Download and its "Mark these
  // N lodged" carry the same `ids`, and every count beside them is that list's
  // length. Whether a pack's mark is offered is packReadiness's call alone —
  // every applicant in it downloaded in this browser (registrar ruling
  // 2026-09-17; lodgement-card.ts). A one-pack queue is the same shape as a
  // glut, one group instead of three.
  const lodgeable = items.filter((i) => i.lodgeable);
  const notLodgeable = items.filter((i) => !i.lodgeable);
  const lodgeableCount = lodgeable.length;
  const ofQueue = `(${lodgeableCount} of ${items.length})`;
  const sizeKnown = isPackPageSize(packPageSize);
  const pages: PackPage<LodgementItem>[] = sizeKnown ? chunkPackPages(lodgeable, packPageSize) : [];
  const paged = pages.length > 1;
  const now = new Date();
  const downloadStyle = {
    fontSize: 13,
    color: 'var(--color-link)',
    textDecoration: 'underline',
  } as const;
  // #666 is 5.74:1 on white (the cards) and 5.18:1 on the page surface
  // #f6f3ec (the privacy line below, which sits outside any card) — both pass
  // WCAG 2.2 SC 1.4.3's 4.5:1. #888 was 3.54:1 on white and failed.
  const muted = { fontSize: 13, color: '#666' } as const;
  const groupHead: React.CSSProperties = { ...CARD, padding: '12px 16px', marginBottom: 8 };
  const groupTitle = { fontSize: 14, fontWeight: 600, margin: 0 } as const;
  const privacyLine = (
    <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
      Each pack holds applicants' personal details and signed consents. Delete it from Downloads
      once they are lodged.
    </p>
  );

  /**
   * A row's own "Mark lodged" stays UNGATED by the download rule — it is the
   * path for an application lodged by hand — but is secondary, so each group
   * has one green button. It asks before it writes (rowLodgeConfirmText), and
   * is off while any mark is in flight (rowMarkDisabled). In a compact pack
   * row it is a borderless link: a secondary control is discrete.
   *
   * It is ALSO off for a row the move has just been refused on as already
   * lodged or no longer on file (Miranda S-1): the server has told us this row
   * is not what the list says it is, so the mark would 409 in its turn.
   */
  /**
   * A row's notes (cca-49qxu): one line each, "Note: …", in the muted ink
   * (Miranda r1 M-5/M-9), under an id the row's document link and "Mark
   * lodged" are described by (M-11), so a screen reader hears them on the
   * controls that act on the row. Rendered once per row at a time — by the
   * compact line, or by the card when that line is expanded.
   */
  const rowNotesId = (item: LodgementItem): string | undefined =>
    rowNotesText(item).length > 0 || rowConsentWarning(item) !== null
      ? `lodgement-notes-${item.id}`
      : undefined;
  /**
   * The notes block. A consent too old for NZPVS (cca-uzt5) is its first line,
   * drawn in the warning pill and pointing at the next step the estate has —
   * the application, as the missing-consent note does (Miranda M-S2). It is a
   * warning, not a block: Copy and Mark lodged stay (registrar, 29 Oct 2026).
   */
  const renderRowNotes = (item: LodgementItem, style: React.CSSProperties) => {
    const warning = rowConsentWarning(item);
    return (
      <div id={rowNotesId(item)} style={{ fontSize: 12, color: 'var(--color-muted)', ...style }}>
        {warning !== null && (
          <p style={{ ...WARN_PILL, margin: '0 0 2px' }}>
            {warning}{' '}
            <a
              href={`/api/vetting/application/${item.id}`}
              target="_blank"
              rel="noopener"
              style={WARN_PILL_LINK}
            >
              see the application&nbsp;↗
            </a>
          </p>
        )}
        {rowNotesText(item).map((line, i) => (
          <p key={i} style={{ margin: 0 }}>
            {line}
          </p>
        ))}
      </div>
    );
  };

  /**
   * "Copy for NZPVS" (cca-uzt5) is offered on a LODGEABLE row only — the worker
   * refuses any other — and not on a row the server has just called out of
   * date. It is a discrete link beside the document link, never beside "Mark
   * lodged": in the card it sits in the content column, which wraps on its own,
   * and in a compact line it comes before the mark's `margin-left: auto`, so
   * the mark keeps its place in both.
   */
  const canCopy = (item: LodgementItem): boolean => item.lodgeable && !staleRows.has(item.id);

  const rowMarkButton = (item: LodgementItem, compact: boolean) => {
    const stale = staleRows.has(item.id);
    return (
      // One flex item either way, so a stale row's note sits UNDER its own
      // control instead of being spread across the card by space-between.
      //
      // `maxWidth` is what lets the note WRAP. `flexShrink: 0` keeps the mark
      // at its full width, but it also pins this item at its max-content size,
      // and the note is one long sentence: at 390 that measured 365px inside a
      // card ending at 366, i.e. 48px of horizontal PAGE overflow on one
      // unwrapped line. `max-width` is applied after flex sizing, so it clamps
      // the item to the card without letting the button shrink.
      <span
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
          flexShrink: 0,
          maxWidth: '100%',
        }}
      >
        <button
          type="button"
          className={compact ? 'btn btn-link btn-sm' : 'btn btn-ghost btn-sm'}
          disabled={stale || rowMarkDisabled(item, busy)}
          title={
            stale
              ? STALE_ROW_NOTE
              : item.lodgeable
                ? undefined
                : `Not lodgeable — missing ${item.missingLabels.join(', ')}. Fill it in, then this returns.`
          }
          onClick={() => void handleLodge(item)}
          aria-describedby={rowNotesId(item)}
          style={{ flexShrink: 0 }}
        >
          {busy === item.id ? 'Marking…' : 'Mark lodged'}
        </button>
        {stale && (
          <span style={{ fontSize: 12, color: 'var(--color-warn, #854f0b)', textAlign: 'right' }}>
            {STALE_ROW_NOTE}
          </span>
        )}
      </span>
    );
  };

  /**
   * The full applicant card: every row below one pack, and every row that needs
   * fixing.
   *
   * It is also where "Move to another person" lives — ONE per applicant, and
   * only here, because this is the only renderer that draws an applicant as a
   * card with a footer. In a paged (glut) queue every lodgeable row starts as
   * `renderCompactApplicant`, whose name-and-parish is a disclosure that
   * expands the row in place into THIS card: the move is reached through the
   * card in both views, and there is only ever one of it per applicant.
   */
  const renderApplicantCard = (item: LodgementItem) => (
    <div key={item.id} style={CARD}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        {/* The column takes the room left and its text wraps inside it; the mark
            beside it never shrinks — so a long note never moves "Mark lodged"
            (Miranda r1 M-10). Its basis is 240px, not 0 (Miranda r2): with a
            basis of 0 the row never wrapped, and at 390 every card squeezed its
            content to ~168px beside the button. Now the button sits top-right
            while the card is wide enough, and wraps below, the content full
            width, on a phone. */}
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 2px' }}>{item.applicantName}</p>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 2px' }}>
            {item.jobTitle} — {item.parishName}
          </p>
          {/* #666 on white is 5.74:1; #888 at 12px was 3.54:1 (SC 1.4.3). */}
          <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
            Parish approved {fmtDate(item.approvedAt)}
          </p>
          {!item.lodgeable && (
            // Discrete inline note, not a banner: the row stays in the
            // queue, but "Mark lodged" is now disabled for it
            // (cca-djp1 — a row the export refused was never sent to
            // Police, so it must not be markable as lodged, singly
            // any more than in bulk). The note says which field is
            // missing and where to fix it; the button returns the
            // moment the row becomes lodgeable.
            // Warn tokens are themed (global.css --amber/--amber-lt).
            // The CTA branches: a missing consent (signature or its
            // date) cannot be typed in by the registrar — the note
            // points at the application instead of the editor. (No
            // "re-send the consent link" action exists on this page;
            // the view is the nearest thing that shows the gap.)
            <p style={{ ...WARN_PILL, margin: '6px 0 0' }}>
              Not lodgeable — missing: {item.missingLabels.join(', ')}.{' '}
              {item.missing.some((m) => CONSENT_COLUMNS.includes(m)) ? (
                <>
                  Needs the applicant's signed consent —{' '}
                  <a
                    href={`/api/vetting/application/${item.id}`}
                    target="_blank"
                    rel="noopener"
                    style={WARN_PILL_LINK}
                  >
                    see the application&nbsp;↗
                  </a>
                </>
              ) : (
                <a
                  href={editLinkHref(item.id)}
                  target="_blank"
                  rel="noopener"
                  style={WARN_PILL_LINK}
                >
                  Edit application&nbsp;↗
                </a>
              )}
            </p>
          )}
          {rowNotesId(item) !== undefined &&
            // Discrete and neutral (cca-49qxu): a fact to know before keying the
            // row into NZPVS, never a reason it cannot be lodged.
            renderRowNotes(item, { margin: '6px 0 0' })}
          <p style={{ margin: '8px 0 0' }}>
            <DocLink id={item.id} pdfFileId={item.pdfFileId} describedBy={rowNotesId(item)} />
          </p>
          {canCopy(item) && (
            <div style={{ margin: '6px 0 0' }}>
              <NzpvsCopyButton
                recordId={item.id}
                describedBy={rowNotesId(item)}
                onClipboard={clipboardRow === item.id}
                onCopied={setClipboardRow}
              />
            </div>
          )}
        </div>
        {rowMarkButton(item, false)}
      </div>
      {/* A correction, not a next step: a discrete link BELOW the card, never a
          second button beside "Mark lodged" (ADR-064 §9). This is the last
          point at which the link can be moved — lodging is what closes the
          window, and the control refuses itself once the row is lodged. */}
      <div style={{ marginTop: 12 }}>
        <RelinkControl
          recordId={item.id}
          parishName={item.parishName}
          applicantName={item.applicantName}
          onAttempt={() => setNotice('')}
          // The refusals that are facts about the LIST, not just about the move
          // (Miranda S-1): this queue holds only `Parish Approved Pending
          // Lodging`, so a row the server calls already lodged — or no longer
          // on file — is a row whose own "Mark lodged" would be refused too.
          // `unavailable` is NOT here: a scrubbed typed application stops the
          // move, but says nothing about whether the row can be lodged.
          onRefused={(kind: RelinkOutcomeKind) => {
            if (kind !== 'already_lodged' && kind !== 'not_found') return;
            setStaleRows((prev) => {
              if (prev.has(item.id)) return prev;
              const next = new Set(prev);
              next.add(item.id);
              return next;
            });
          }}
          onMoved={(message) => {
            setNotice(message);
            // `load()` ONLY — deliberately NOT `onRefresh()`.
            //
            // `onRefresh` bumps the page's `refreshKey`, which is the key on
            // the <div> wrapping every section (RegistrarDashboardPage), so
            // calling it REMOUNTS this section and everything in it: the
            // notice above is discarded, the chooser is torn down and the
            // control's ref is replaced, so nothing is announced and focus
            // lands nowhere. That is the same trap the identity review section
            // is already documented to avoid — it bumps `statsKey` alone for
            // exactly this reason.
            //
            // Nothing on the stats bar moves anyway: a move changes no
            // Blueprint status and closes no queue row, so there is no count
            // to re-read. `load()` refreshes this queue in place (the row's
            // name follows the new Contact) without touching `loaded`, so no
            // Loading swap either.
            void load();
          }}
        />
      </div>
    </div>
  );

  /**
   * At glut size a lodgeable applicant is one compact line (Miranda, round 3).
   * No approval date here (round 7): the CSV carries it, and without it a row
   * fits one line at 1280.
   *
   * The name and parish ARE that line's disclosure (Miranda's ruling, round 7).
   * Pressing them expands this one row in place into `renderApplicantCard` —
   * the card that already carries "Move to another person" in its footer,
   * exactly where ADR-064 §9 puts it. Until this, the move existed on no screen
   * once the queue spanned more than one pack: `paged` is true from 21
   * applicants, every lodgeable row was then compact, and the only other mount
   * (VettingQueuePage's "Awaiting your approval" card) is a state a lodgeable
   * record has already left.
   *
   * Nothing here is new. The disclosure is the identity review's own row
   * control — `.idr-item-head` + `.idr-caret`, with its 44px floor, its ▸/▾ and
   * its focus ring — and the only addition to the stylesheet is the modifier
   * that lets the head sit on a line beside the row's other controls instead of
   * taking the full width. One row is open at a time, and no second control
   * appears beside "Mark lodged": while a row is open the compact line sheds
   * its document link and its mark, because the card below carries both.
   */
  const renderCompactApplicant = (item: LodgementItem) => {
    const expanded = expandedRow === item.id;
    const panelId = `lodgement-row-${item.id}`;
    return (
      <div key={item.id}>
        <div
          // `lodgement-compact-row`: global.css moves the notes up under the
          // name on a phone (≤640px) — name, notes, actions (Miranda r1 M-9).
          className="lodgement-compact-row"
          style={{
            ...CARD,
            // No vertical padding of its own: the head button carries the 44px
            // floor and its own 10px, so a row stays one line at 1280.
            padding: '0 16px',
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          {/* h4, not h3: the pack's own heading above these rows is the h3, so
              the outline runs h1 → h2 (the section) → h3 (the pack) → this. */}
          <h4 className="idr-item-head idr-item-head-row">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => setExpandedRow(expanded ? null : item.id)}
            >
              <span aria-hidden="true" className="idr-caret">
                {expanded ? '▾' : '▸'}
              </span>
              <span className="idr-item-name">{item.applicantName}</span>
              <span className="idr-item-sub">{item.parishName}</span>
            </button>
          </h4>
          {!expanded && (
            <>
              <DocLink id={item.id} pdfFileId={item.pdfFileId} describedBy={rowNotesId(item)} />
              {canCopy(item) && (
                // `inline`: the button is a flex item of this line, and its status
                // a full-width line UNDER the row (Miranda M-S3), so the one-line
                // row stays one line.
                <NzpvsCopyButton
                  layout="inline"
                  recordId={item.id}
                  describedBy={rowNotesId(item)}
                  onClipboard={clipboardRow === item.id}
                  onCopied={setClipboardRow}
                />
              )}
              <span style={{ marginLeft: 'auto' }}>{rowMarkButton(item, true)}</span>
              {/* The notes show on the compact row too (cca-49qxu): at glut size
                  nobody expands a lodgeable row before keying it. A full-width
                  block UNDER the approved one-line head, which stays intact
                  (Miranda r1 M-9): the head's own caret, hidden, and its gap
                  and padding set the indent, so the notes start where the
                  name's text does. On a phone global.css orders this block
                  straight after the name (`lodgement-row-notes`). */}
              {rowNotesId(item) !== undefined && (
                <div
                  className="lodgement-row-notes"
                  style={{ flexBasis: '100%', display: 'flex', gap: 8, padding: '0 4px 10px' }}
                >
                  <span aria-hidden="true" className="idr-caret" style={{ visibility: 'hidden' }}>
                    ▸
                  </span>
                  {renderRowNotes(item, { minWidth: 0 })}
                </div>
              )}
            </>
          )}
        </div>
        {expanded && <div id={panelId}>{renderApplicantCard(item)}</div>}
      </div>
    );
  };

  /**
   * A refusal as lists (Miranda, round 3), announced as it appears
   * (role="alert", WCAG 2.2 SC 4.1.3). `retry` puts "Try download again"
   * inside the message; it is null when the pack is already downloaded and
   * "Download again" sits in the heading instead. `packKey` files the retry
   * button so focus can move to it once the refusal renders (SC 2.4.3).
   */
  const renderFailure = (failure: PackFailureView, retry: (() => void) | null, packKey: string) => {
    const list = (lines: PackFailureLine[]) => (
      <ul style={{ margin: '2px 0 6px', paddingLeft: 20 }}>
        {lines.map((line) => (
          <li key={line.id}>
            {line.name} — {line.reason}
          </li>
        ))}
      </ul>
    );
    return (
      <div role="alert" style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>
        <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{failure.headline}</p>
        {failure.note && <p style={{ margin: '0 0 6px' }}>{failure.note}</p>}
        {failure.retry.length > 0 && (
          <>
            <p style={{ margin: 0 }}>{RETRY_LIST_HEADING}</p>
            {list(failure.retry)}
          </>
        )}
        {failure.stuck.length > 0 && (
          <>
            <p style={{ margin: 0 }}>{STUCK_LIST_HEADING}</p>
            {list(failure.stuck)}
          </>
        )}
        {retry && (
          <button
            ref={fileUnder(retryButtons.current, packKey)}
            type="button"
            className="btn btn-primary btn-sm"
            disabled={downloading !== null}
            onClick={retry}
          >
            Try download again
          </button>
        )}
      </div>
    );
  };

  /**
   * One pack's group: its controls in the heading, its applicants beneath.
   * Three states in shape and in words — not downloaded (Download pack is the
   * green button, no mark), refused (the message, with Try download again
   * inside it), downloaded (a "Downloaded 10:42am" pill, Download again as a
   * link, and the mark as the green button).
   */
  const renderPack = (page: PackPage<LodgementItem>) => {
    const failure = packErrors[page.key];
    const ready = packReadiness({
      ids: page.ids,
      key: page.key,
      downloads,
      downloadingKey: downloading,
      errors: packErrors,
    });
    const headingId = `lodgement-pack-${page.number}`;
    // Prefixed: a one-row pack's key is that row's id, which the row's own
    // button already uses.
    const markKey = `pack:${page.key}`;
    const download = () => {
      if (downloading === null) void handleDownload(page);
    };
    // Both marks post page.ids — the same list the download carries. The
    // escape changes the confirm's words, not the set (packMarkConfirmOptions).
    const mark = (control: PackMarkControl) =>
      void handleLodgeSet(
        markKey,
        page.items,
        lodgeConfirmText(
          packMarkConfirmOptions(control, {
            page,
            left: notLodgeable,
            paged,
            lodgeableTotal: lodgeableCount,
          }),
        ),
      );
    return (
      <section key={page.key} aria-labelledby={headingId} style={{ marginBottom: 20 }}>
        <div style={groupHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {/* tabIndex -1: focus lands here when no Mark button can take it. */}
            <h3
              id={headingId}
              ref={fileUnder(packHeadings.current, page.key)}
              tabIndex={-1}
              style={groupTitle}
            >
              {packPageLabel(page)}
            </h3>
            {ready.downloadedAt && (
              <span className="pill pill-brand">{downloadedLabel(ready.downloadedAt, now)}</span>
            )}
            {/* Always mounted, so both of its messages are announced (SC 4.1.3). */}
            <span role="status" style={muted}>
              {ready.downloading
                ? 'Preparing pack…'
                : announcement?.key === page.key
                  ? announcement.text
                  : ''}
            </span>
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              {ready.status === 'downloaded' ? (
                <>
                  <button
                    type="button"
                    className="btn btn-link"
                    style={{ fontSize: 13 }}
                    disabled={downloading !== null}
                    onClick={download}
                  >
                    Download again
                  </button>
                  <button
                    ref={fileUnder(markButtons.current, page.key)}
                    type="button"
                    className="btn btn-primary"
                    disabled={markDisabled(ready, busy)}
                    title={
                      ready.downloading
                        ? 'Wait for the pack to finish downloading.'
                        : 'Marks the applicants this pack holds. A download is not a lodgement — mark them once they are submitted in NZPVS.'
                    }
                    onClick={() => mark('mark')}
                  >
                    {busy === markKey ? 'Marking…' : pageMarkLabel(page.items.length)}
                  </button>
                </>
              ) : (
                <>
                  {ready.status === 'not_downloaded' && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={downloading !== null}
                      onClick={download}
                    >
                      Download pack
                    </button>
                  )}
                  <span
                    style={muted}
                    title="Offered once everyone in this pack has been downloaded in this browser."
                  >
                    Mark after downloading
                  </span>
                </>
              )}
            </div>
          </div>
          {failure &&
            renderFailure(failure, ready.status === 'refused' ? download : null, page.key)}
          {ready.offerEscape && (
            // Quiet on purpose: for packs downloaded on another of his Macs.
            // It requests the unrecorded confirm ("No pack was downloaded in
            // this browser.") — packMarkConfirmOptions('escape').
            <button
              type="button"
              className="btn btn-link"
              style={{ fontSize: 12, marginTop: 6 }}
              disabled={busy !== null}
              onClick={() => mark('escape')}
            >
              Lodged these another way?
            </button>
          )}
        </div>
        {page.items.map((item) =>
          paged ? renderCompactApplicant(item) : renderApplicantCard(item),
        )}
      </section>
    );
  };

  return (
    <div style={{ marginBottom: 40 }}>
      <CollapsibleHead
        title="Ready to lodge with NZPV"
        // Not a count while the queue failed to load (cca-zbmdy): "0" and
        // "(0 of 0)" would say there is nothing to lodge.
        count={loadError ? '…' : items.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
        headingRef={headingRef}
        right={
          // The worksheet alone stays a plain link: it holds no consent PDFs
          // and marks nothing. Every pack's controls live in its own group.
          <a href="/api/vetting/registrar/lodgement-queue/csv" style={downloadStyle}>
            {loadError ? 'Download CSV' : `Download CSV ${ofQueue}`}
          </a>
        }
      />
      {/* OUTSIDE the collapse, and outside every conditional: a live region has
          to be in the document BEFORE it gains text or the text is not reliably
          announced (SC 4.1.3). It used to sit inside `{open && …}`, which was
          present on first render only because the section happens to default
          open — one collapse and the announcement would have gone silent. */}
      <p className="idr-status" role="status" aria-live="polite">
        {notice}
      </p>
      {open && (
        <>
          {error && (
            <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}
          {loadError && <LoadFailure message={loadError} onRetry={() => void load(true)} />}
          {!sizeKnown && items.length > 0 && (
            <p style={{ fontSize: 13, color: 'var(--red)', margin: '0 0 12px' }}>
              The pack size could not be read, so no pack or pack mark is offered. Reload the page.
            </p>
          )}
          {items.length === 0 ? (
            // Not when the load failed (cca-zbmdy): that is not "nothing to lodge".
            !loadError && <Empty msg="No applications awaiting lodgement." />
          ) : (
            <>
              {notLodgeable.length > 0 && (
                // First, so what blocks a lodgement is seen before the packs.
                <section aria-labelledby="lodgement-needs-fixing" style={{ marginBottom: 20 }}>
                  <div style={groupHead}>
                    <h3 id="lodgement-needs-fixing" style={groupTitle}>
                      Needs fixing first ({notLodgeable.length})
                    </h3>
                    <p style={{ ...muted, margin: '4px 0 0' }}>
                      In no pack yet — each needs what its note names before it can be lodged.
                    </p>
                  </div>
                  {notLodgeable.map(renderApplicantCard)}
                </section>
              )}
              {pages.length > 0 && privacyLine}
              {pages.map(renderPack)}
              {/* Pack size unreadable: no packs, but each row's own mark still works. */}
              {!sizeKnown && lodgeable.map(renderApplicantCard)}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Section 2: Awaiting result ─────────────────────────────────────────────

function AwaitingResultSection() {
  const [items, setItems] = useState<AwaitingResultItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const nextRequest = useLatestRequest();
  const { headingRef, focusHeading } = useHeadingFocus();

  /** The first load, and "Try again". A retry leaves `loaded` alone, so it
   *  never swaps the section back to "Loading…". Only the latest load's answer
   *  is applied (useLatestRequest); a successful retry focuses the heading. */
  function load(retry = false) {
    const isLatest = nextRequest();
    apiFetch<{ queue: AwaitingResultItem[] }>('/api/vetting/registrar/awaiting-result')
      .then((d) => {
        if (!isLatest()) return;
        setItems(d.queue);
        setError(null);
        if (retry) focusHeading();
      })
      .catch(() => {
        if (!isLatest()) return;
        setError('Could not load the applications awaiting a result.');
      })
      .finally(() => setLoaded(true));
  }

  useEffect(() => {
    load();
  }, []);

  if (!loaded) return <p style={{ color: '#666', fontSize: 14 }}>Loading…</p>;

  return (
    <div style={{ marginBottom: 40 }}>
      <CollapsibleHead
        title="Awaiting result from NZPV"
        count={error ? '…' : items.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
        headingRef={headingRef}
      />
      {open && (
        <>
          {/* --color-muted (@cca/ui tokens.css), not #888. At 12px on this portal's cream
              #888 measures 3.43:1 — below the SC 1.4.3 floor of 4.5:1 — and the
              line below it is not decoration: it is the SUBSTITUTE for a
              control, the only place the registrar is told why the move is not
              offered here. Its neighbour rides the same repair because they are
              one block of helper text. The "Lodged … · Ref:" line below rides
              it too (Miranda S5 — the reference number is read back to Police).
              (The page's REMAINING #888 sites — the parish-links helper and the
              reconcile counts — are pre-existing and beaded, not swept here.) */}
          <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '-6px 0 4px' }}>
            Results are recorded automatically when the NZPV notification arrives — anything needing
            your attention appears under "Results needing attention".
          </p>
          {/* The answer to "can I still move this one?", stated ONCE for the
              section rather than as a control on every row that could only
              ever refuse. The window closed when these were lodged, and the
              reason is the whole point of the lock (ADR-064 §9). */}
          <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '0 0 12px' }}>
            {RELINK.locked}
          </p>
          {error ? (
            // Instead of the empty sentence, not beside it (cca-zbmdy): a
            // failed fetch is not "nothing awaiting a result".
            <LoadFailure message={error} onRetry={() => load(true)} />
          ) : items.length === 0 ? (
            <Empty msg="No applications awaiting a result." />
          ) : (
            items.map((item) => (
              <div key={item.id} style={CARD}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 2px' }}>
                    {item.applicantName}
                  </p>
                  <p style={{ fontSize: 13, color: '#666', margin: '0 0 2px' }}>
                    {item.jobTitle} — {item.parishName}
                  </p>
                  {/* --color-muted, not #888 (Miranda S5): the NZPV reference
                      number is the one string on this page the registrar reads
                      back to Police over the phone, and at 12px #888 measured
                      3.54:1 on white — an SC 1.4.3 failure, and the least
                      legible text on the screen. Same token as the two helper
                      lines above. */}
                  <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: 0 }}>
                    Lodged {fmtDate(item.lodgedAt)}
                    {item.nzpvRequestId ? ` · Ref: ${item.nzpvRequestId}` : ''}
                  </p>
                  <p style={{ margin: '8px 0 0' }}>
                    <DocLink id={item.id} pdfFileId={item.pdfFileId} />
                  </p>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}

// ── Results needing attention (non-clear, auto-recorded by the cron) ────────

const FLAG_LABELS: Record<string, string> = {
  released_with_results: 'Released with results',
  rejected_and_closed: 'Rejected and closed',
};

function ResultFlagsSection() {
  const [flags, setFlags] = useState<ResultFlag[]>([]);
  const [loaded, setLoaded] = useState(false);
  /**
   * The flags could not be fetched (cca-zbmdy, UX-7). This section's empty
   * state is no section at all, so a failure that stayed hidden read as "no
   * result needs attention" — and the awaiting section's helper line sends the
   * registrar here for exactly those results.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * The flags on screen came from "Try again" (cca-zbmdy r1). An empty answer
   * then keeps the section, saying "Nothing needs attention." — vanishing would
   * confirm nothing and leave focus nowhere. At rest an empty section stays
   * hidden, as before: any other load (the first, a dismiss's) clears this.
   */
  const [fromRetry, setFromRetry] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const nextRequest = useLatestRequest();
  const { headingRef, focusHeading } = useHeadingFocus();

  /** `retry`: started by "Try again". Only the latest load's answer is applied. */
  async function load(retry = false) {
    const isLatest = nextRequest();
    try {
      const d = await apiFetch<{ flags: ResultFlag[] }>('/api/vetting/registrar/result-flags');
      if (!isLatest()) return;
      setFlags(d.flags);
      setLoadError(null);
      setFromRetry(retry);
      if (retry) focusHeading();
    } catch {
      if (!isLatest()) return;
      setLoadError('Could not load the results needing attention.');
    }
    setLoaded(true);
  }

  useEffect(() => {
    void load();
  }, []);

  async function dismiss(recordId: string) {
    setBusy(recordId);
    try {
      await apiPost(`/api/vetting/registrar/result-flags/${recordId}/dismiss`, {});
      await load();
    } catch {
      /* leave flag visible */
    } finally {
      setBusy(null);
    }
  }

  // Hidden while loading and when there is nothing to attend to — but not when
  // the fetch failed, nor straight after a retry.
  if (!loaded || (!loadError && flags.length === 0 && !fromRetry)) return null;

  return (
    <div style={{ marginBottom: 40 }}>
      <SectionHead
        title="Results needing attention"
        count={loadError ? '…' : flags.length}
        headingRef={headingRef}
      />
      {/* Mounted, empty, whenever the section is — so it is already in the
          document while the error shows, and "Nothing needs attention." is
          announced when a retry brings it (SC 4.1.3; a region mounted WITH its
          text is not reliably announced). Empty, it takes no height. */}
      <p
        role="status"
        aria-live="polite"
        style={{ fontSize: 14, color: 'var(--color-muted)', margin: 0 }}
      >
        {!loadError && fromRetry && flags.length === 0 ? 'Nothing needs attention.' : ''}
      </p>
      {loadError && <LoadFailure message={loadError} onRetry={() => void load(true)} />}
      {flags.map((f) => (
        <div
          key={f.recordId}
          style={{
            ...CARD,
            borderLeft: '4px solid var(--amber, #854f0b)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 2px' }}>{f.applicantName}</p>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 2px' }}>
              {FLAG_LABELS[f.flagType] ?? f.flagType} · NZPV ID {f.nzpvApplicationId} ·{' '}
              {fmtDate(f.createdAt)}
            </p>
            {f.reason && <p style={{ fontSize: 13, color: '#92400e', margin: 0 }}>{f.reason}</p>}
            <p style={{ margin: '8px 0 0' }}>
              <ViewAppLink id={f.recordId} />
            </p>
          </div>
          <button
            className="btn btn-ghost"
            disabled={busy === f.recordId}
            onClick={() => void dismiss(f.recordId)}
            style={{ flexShrink: 0 }}
          >
            {busy === f.recordId ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Expired results ─────────────────────────────────────────────────────────

interface ExpiredItem {
  id: string;
  applicantName: string;
  contactId: string;
  parishId: string;
  parishName: string;
  jobTitle: string;
  expiresAt: string | null;
  /** Why this person can be skipped: an in-flight renewal, a current vetting
   *  elsewhere, or null (genuinely needs chasing). */
  skipReason: 'in_flight' | 'covered' | null;
  /** For 'covered', when that current vetting expires (ISO date). */
  coverUntil: string | null;
  /** For 'covered', the parish that current vetting sits under. */
  coverParish: string | null;
}

/** The greyed note shown for a skippable expired person — null if not skippable. */
function skipLabel(i: ExpiredItem): string | null {
  if (i.skipReason === 'in_flight') return 'renewal in progress';
  if (i.skipReason === 'covered') {
    const where = i.coverParish ? ` at ${i.coverParish}` : '';
    return `currently vetted${where} · exp ${fmtDate(i.coverUntil)}`;
  }
  return null;
}
interface ExpiredParish {
  parishId: string;
  parishName: string;
  count: number;
  vpsoName: string | null;
  lastNotifiedAt: string | null;
}
interface ExpiredResponse {
  count: number;
  items: ExpiredItem[];
  parishes: ExpiredParish[];
  reconcile: ExpiredItem[];
  reconcileCount: number;
}

/** "2y 3m ago" / "under a month" from an expiry date. */
function expiredAgo(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return iso;
  const months = Math.max(0, Math.floor((Date.now() - then) / (30.44 * 24 * 3600 * 1000)));
  if (months < 1) return 'under a month';
  const y = Math.floor(months / 12);
  const m = months % 12;
  return `${[y ? `${y}y` : '', m ? `${m}m` : ''].filter(Boolean).join(' ')} ago`;
}

function defaultMessage(parishName: string, vpsoName: string | null): string {
  return `Kia ora ${vpsoName?.split(' ')[0] ?? 'team'},

The following members of ${parishName} have a Police vetting that has now expired and needs renewal. Please arrange for each person to complete a new vetting consent form at your earliest convenience.

Ngā mihi,
The Registrar`;
}

interface ComposeState {
  parish: ExpiredParish;
  people: ExpiredItem[];
  /** Person ids the registrar has unchecked (e.g. renewal already in flight). */
  excluded: Set<string>;
  vpsoEmail: string | null;
  vpsoLoading: boolean;
  subject: string;
  message: string;
}

function ExpiredSection() {
  const [data, setData] = useState<ExpiredResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** A consolidate's Drive half when it needs the registrar (cca-vz2o): its own
   *  "Needs attention:" line under the green notice, in the same live region,
   *  with the kept folder's link. */
  const [driveWarning, setDriveWarning] = useState<DriveMoveNote | null>(null);
  /** Which section's live region shows the notice (Miranda r2 S1): the one
   *  whose button produced it, so the confirmation and any Drive warning sit
   *  together beside that button and only one region ever holds text. */
  const [noticeFrom, setNoticeFrom] = useState<'expired' | 'reconcile'>('expired');
  const [view, setView] = useState<'flat' | 'parish'>('flat');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'overdue' | 'name' | 'parish'>('overdue');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [sending, setSending] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [consolidating, setConsolidating] = useState<string | null>(null);

  function load() {
    setLoading(true);
    apiFetch<ExpiredResponse>('/api/vetting/registrar/expired')
      .then(setData)
      .catch(() => setError('Could not load expired results.'))
      .finally(() => setLoading(false));
  }

  // Load on mount so the count is visible without expanding (matches the other
  // sections); the body stays collapsed until Show.
  useEffect(() => {
    load();
  }, []);

  function toggleParish(id: string) {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function openCompose(parish: ExpiredParish) {
    if (!data) return;
    const people = data.items.filter((i) => i.parishId === parish.parishId);
    setNotice(null);
    setDriveWarning(null);
    setError(null);
    setCompose({
      parish,
      people,
      // Auto-skip anyone whose renewal is already in flight; the registrar can
      // re-tick to override.
      excluded: new Set(people.filter((p) => p.skipReason !== null).map((p) => p.id)),
      vpsoEmail: null,
      vpsoLoading: true,
      subject: `Expired Police vetting — action needed (${parish.parishName})`,
      message: defaultMessage(parish.parishName, parish.vpsoName),
    });
    try {
      const r = await apiFetch<{ vpso: { name: string; email: string } | null }>(
        `/api/vetting/registrar/expired/parish/${parish.parishId}/vpso`,
      );
      setCompose((c) =>
        c && c.parish.parishId === parish.parishId
          ? { ...c, vpsoEmail: r.vpso?.email ?? null, vpsoLoading: false }
          : c,
      );
    } catch {
      setCompose((c) =>
        c && c.parish.parishId === parish.parishId ? { ...c, vpsoLoading: false } : c,
      );
    }
  }

  async function sendNotice() {
    if (!compose) return;
    setSending(true);
    setError(null);
    try {
      const includeIds = compose.people.filter((p) => !compose.excluded.has(p.id)).map((p) => p.id);
      const r = await apiPost<{ to: string; count: number }>(
        '/api/vetting/registrar/expired/notify-vpso',
        {
          parishId: compose.parish.parishId,
          subject: compose.subject,
          message: compose.message,
          includeIds,
        },
      );
      setNoticeFrom('expired');
      setNotice(
        `Sent to ${compose.parish.vpsoName ?? r.to} — ${r.count} ${r.count === 1 ? 'person' : 'people'} at ${compose.parish.parishName}.`,
      );
      setCompose(null);
      load(); // refresh "last notified"
    } catch (e) {
      const err = e as { status?: number; data?: { error?: string } };
      if (err.status === 422 && err.data?.error === 'no_vpso') {
        setError(`${compose.parish.parishName} has no VPSO email on file — add one in Zoho first.`);
      } else {
        setError('Could not send the notice. Try again.');
      }
    } finally {
      setSending(false);
    }
  }

  // One-click tidy-up: repoint the person's stale records to their current
  // parish, fix the name, re-home the Drive folder, mark reconciled.
  async function handleConsolidate(contactId: string, parishName: string, name: string) {
    setConsolidating(contactId);
    setError(null);
    setNotice(null);
    setDriveWarning(null);
    try {
      const r = await apiPost<{
        recordsUpdated: number;
        drive: string;
        driveDetail?: DriveMoveDetail | null;
        folderIdSaved?: boolean;
      }>('/api/vetting/registrar/expired/consolidate', { contactId });
      // Every Drive outcome says something (OBS-9): a good one rides the green
      // notice; one that needs the registrar gets its own line (cca-vz2o).
      const note = driveMoveNote(r.drive, r.driveDetail, r.folderIdSaved);
      const driveText = note && !note.warn ? ` ${note.text}` : '';
      setNoticeFrom('reconcile');
      setNotice(`${name} consolidated to ${parishName}.${driveText}`);
      setDriveWarning(note?.warn ? note : null);
      load();
    } catch {
      setError(`Could not consolidate ${name}. Try again.`);
    } finally {
      setConsolidating(null);
    }
  }

  const q = search.trim().toLowerCase();
  const matches = (i: ExpiredItem) =>
    !q || i.applicantName.toLowerCase().includes(q) || i.parishName.toLowerCase().includes(q);

  /** The notice and its Drive warning, drawn ONLY in the section they came
   *  from (Miranda r2 S1); the other section's region stays empty. */
  function noticeFor(section: 'expired' | 'reconcile') {
    if (noticeFrom !== section) return null;
    return (
      <>
        {notice && (
          <div className="idr-status" style={{ margin: '0 0 12px' }}>
            {notice}
          </div>
        )}
        {driveWarning && (
          <div className="idr-danger" style={{ fontSize: 13, marginBottom: 12 }}>
            <strong>Needs attention:</strong> {driveWarning.text}
            {/* Its own line, out of the sentence (Miranda r2 S2); the visible
                text is the start of the accessible name (SC 2.5.3). */}
            {driveWarning.folderUrl && (
              <span className="idr-link-line">
                <a
                  className="idr-link"
                  href={driveWarning.folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open the old Drive folder (opens in a new tab)"
                >
                  Open the old Drive folder ↗
                </a>
              </span>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <CollapsibleHead
          title="Expired results"
          count={data?.count ?? '…'}
          open={open}
          onToggle={() => setOpen((o) => !o)}
        />

        {/* This section's live region for the notice AND the Drive warning
            (cca-vz2o), OUTSIDE the collapse and every conditional, empty until
            it has text — as the lodgement section's status line above: a region
            mounted WITH its text is not reliably announced (SC 4.1.3). */}
        <div role="status" aria-live="polite">
          {noticeFor('expired')}
        </div>

        {open && (
          <div>
            {error && (
              <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}

            {loading && !data && <p style={{ color: '#666', fontSize: 14 }}>Loading…</p>}

            {data && data.count === 0 && <Empty msg="No expired vetting results." />}

            {data && data.count > 0 && (
              <>
                {/* Controls */}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{ display: 'inline-flex', border: '1px solid #d6d3cb', borderRadius: 6 }}
                  >
                    {(['flat', 'parish'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setView(v)}
                        style={{
                          fontSize: 12,
                          padding: '6px 12px',
                          border: 'none',
                          cursor: 'pointer',
                          background: view === v ? '#1e4d3f' : 'white',
                          color: view === v ? 'white' : '#444',
                          fontWeight: view === v ? 600 : 400,
                        }}
                      >
                        {v === 'flat' ? 'Flat list' : 'By parish'}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name or parish…"
                    style={{
                      flex: '1 1 180px',
                      fontSize: 13,
                      padding: '7px 10px',
                      border: '1px solid #d6d3cb',
                      borderRadius: 6,
                    }}
                  />
                  {view === 'flat' && (
                    <select
                      value={sort}
                      onChange={(e) => setSort(e.target.value as typeof sort)}
                      style={{
                        fontSize: 12,
                        padding: '7px 10px',
                        border: '1px solid #d6d3cb',
                        borderRadius: 6,
                      }}
                    >
                      <option value="overdue">Most overdue</option>
                      <option value="name">Name</option>
                      <option value="parish">Parish</option>
                    </select>
                  )}
                </div>

                {view === 'flat' ? (
                  <ExpiredFlatTable items={data.items.filter(matches)} sort={sort} />
                ) : (
                  <ExpiredByParish
                    parishes={data.parishes}
                    items={data.items.filter(matches)}
                    expanded={expanded}
                    onToggle={toggleParish}
                    compose={compose}
                    sending={sending}
                    onCompose={openCompose}
                    onCancelCompose={() => setCompose(null)}
                    onChangeCompose={(patch) => setCompose((c) => (c ? { ...c, ...patch } : c))}
                    onSend={sendNotice}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Reconcile — person is vetted elsewhere but has a stale expired record */}
      <div id="sec-reconcile" style={{ marginBottom: 12 }}>
        <CollapsibleHead
          title="Reconcile — currently vetted elsewhere"
          count={data?.reconcileCount ?? '…'}
          open={reconcileOpen}
          onToggle={() => setReconcileOpen((o) => !o)}
        />
        {/* Consolidate's own live region (Miranda r2 S1), outside the collapse:
            its confirmation and Drive warning land beside the clicked button. */}
        <div role="status" aria-live="polite">
          {noticeFor('reconcile')}
        </div>
        {reconcileOpen && data && (
          <>
            {error && (
              <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
              These people hold a current vetting elsewhere, so they're off the chase list.
              Consolidate moves their stale record + Drive folder to their current parish and tidies
              it (nothing is deleted).
            </p>
            {data.reconcile.length === 0 ? (
              <Empty msg="Nothing to reconcile." />
            ) : (
              data.reconcile.map((i) => (
                <div
                  key={i.id}
                  style={{
                    ...CARD,
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ fontWeight: 600, fontSize: 14, margin: '0 0 2px' }}>
                      {i.applicantName}
                    </p>
                    <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                      Stale record at {i.parishName} · expired {fmtDate(i.expiresAt)}
                    </p>
                    <p style={{ fontSize: 12, color: '#166534', margin: '2px 0 0' }}>
                      Currently vetted at {i.coverParish ?? '—'} · exp {fmtDate(i.coverUntil)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      void handleConsolidate(
                        i.contactId,
                        i.coverParish ?? 'current parish',
                        i.applicantName,
                      )
                    }
                    disabled={consolidating === i.contactId || !i.contactId}
                    style={{
                      background: '#1e4d3f',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      padding: '8px 16px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: consolidating === i.contactId ? 'default' : 'pointer',
                      opacity: consolidating === i.contactId ? 0.7 : 1,
                    }}
                  >
                    {consolidating === i.contactId
                      ? 'Consolidating…'
                      : `Consolidate to ${i.coverParish ?? 'current'}`}
                  </button>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}

function ExpiredFlatTable({
  items,
  sort,
}: {
  items: ExpiredItem[];
  sort: 'overdue' | 'name' | 'parish';
}) {
  const sorted = [...items].sort((a, b) => {
    if (sort === 'name') return a.applicantName.localeCompare(b.applicantName);
    if (sort === 'parish')
      return (
        a.parishName.localeCompare(b.parishName) || a.applicantName.localeCompare(b.applicantName)
      );
    // overdue: oldest expiry first
    return (a.expiresAt ?? '').localeCompare(b.expiresAt ?? '');
  });
  if (sorted.length === 0) return <Empty msg="No matches." />;
  const th: React.CSSProperties = {
    textAlign: 'left',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#6b7280',
    padding: '0 12px 8px 0',
    borderBottom: '1px solid #e0e0d8',
  };
  const td: React.CSSProperties = { fontSize: 13, padding: '8px 12px 8px 0', verticalAlign: 'top' };
  return (
    <div style={{ ...CARD, padding: '12px 16px', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Name</th>
            <th style={th}>Parish</th>
            <th style={th}>Role</th>
            <th style={th}>Expired</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((i) => {
            const skip = skipLabel(i);
            return (
              <tr
                key={i.id}
                style={{
                  borderBottom: '1px solid #f0efe9',
                  color: skip ? '#9ca3af' : undefined,
                }}
              >
                <td style={{ ...td, fontWeight: 600 }}>
                  {i.applicantName}
                  {skip && (
                    <span style={{ fontWeight: 400, fontStyle: 'italic', color: '#9ca3af' }}>
                      {' '}
                      · {skip}
                    </span>
                  )}
                </td>
                <td style={{ ...td, color: skip ? '#9ca3af' : '#555' }}>{i.parishName}</td>
                <td style={{ ...td, color: skip ? '#9ca3af' : '#555' }}>{i.jobTitle}</td>
                <td style={{ ...td, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {fmtDate(i.expiresAt)} <span>({expiredAgo(i.expiresAt)})</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExpiredByParish({
  parishes,
  items,
  expanded,
  onToggle,
  compose,
  sending,
  onCompose,
  onCancelCompose,
  onChangeCompose,
  onSend,
}: {
  parishes: ExpiredParish[];
  items: ExpiredItem[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  compose: ComposeState | null;
  sending: boolean;
  onCompose: (p: ExpiredParish) => void;
  onCancelCompose: () => void;
  onChangeCompose: (patch: Partial<ComposeState>) => void;
  onSend: () => void;
}) {
  // Only parishes that still have matching people after the search filter.
  const visible = parishes.filter((p) => items.some((i) => i.parishId === p.parishId));
  if (visible.length === 0) return <Empty msg="No matches." />;
  return (
    <div>
      {visible.map((p) => {
        const people = items.filter((i) => i.parishId === p.parishId);
        const isOpen = expanded.has(p.parishId);
        const composing = compose?.parish.parishId === p.parishId;
        return (
          <div key={p.parishId} style={{ ...CARD, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => onToggle(p.parishId)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 0,
                }}
              >
                <span style={{ color: '#1e4d3f', fontSize: 12 }}>{isOpen ? '▾' : '▸'}</span>
                {p.parishName}
                <span style={{ color: '#888', fontWeight: 400, fontSize: 13 }}>
                  ({people.length})
                </span>
              </button>
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 12, color: p.vpsoName ? '#555' : '#b45309' }}>
                  {p.vpsoName ? `VPSO: ${p.vpsoName}` : 'No VPSO on file'}
                </span>
                {p.lastNotifiedAt && (
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    Notified {fmtDate(p.lastNotifiedAt)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onCompose(p)}
                  disabled={!p.vpsoName || composing}
                  style={{
                    background: p.vpsoName ? '#1e4d3f' : '#cbd5c8',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: !p.vpsoName || composing ? 'default' : 'pointer',
                  }}
                >
                  Email VPSO
                </button>
              </div>
            </div>

            {/* Compose editor */}
            {composing && compose && (
              <div
                style={{
                  marginTop: 14,
                  background: '#faf9f5',
                  border: '1px solid #e0ddd6',
                  borderRadius: 6,
                  padding: '14px 16px',
                }}
              >
                <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
                  To:{' '}
                  {compose.vpsoLoading ? (
                    <em>resolving VPSO…</em>
                  ) : compose.vpsoEmail ? (
                    <strong>{compose.vpsoEmail}</strong>
                  ) : (
                    <span style={{ color: 'var(--red)' }}>no VPSO email on file</span>
                  )}
                </p>
                <input
                  type="text"
                  value={compose.subject}
                  onChange={(e) => onChangeCompose({ subject: e.target.value })}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: 13,
                    padding: '8px 10px',
                    border: '1px solid #d6d3cb',
                    borderRadius: 4,
                    marginBottom: 8,
                  }}
                />
                <textarea
                  value={compose.message}
                  onChange={(e) => onChangeCompose({ message: e.target.value })}
                  rows={6}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: 13,
                    padding: '8px 10px',
                    border: '1px solid #d6d3cb',
                    borderRadius: 4,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <p
                  style={{
                    margin: '10px 0 2px',
                    fontSize: 11,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Included below your message ({compose.people.length - compose.excluded.size} of{' '}
                  {compose.people.length})
                </p>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: '#6b7280' }}>
                  People with a renewal in progress, or already vetted elsewhere, are greyed and
                  unticked automatically — re-tick to include them anyway.
                </p>
                <div style={{ marginBottom: 12 }}>
                  {compose.people.map((i) => {
                    const on = !compose.excluded.has(i.id);
                    const skip = skipLabel(i);
                    const dim = skip !== null || !on;
                    const toggle = () => {
                      const next = new Set(compose.excluded);
                      if (next.has(i.id)) next.delete(i.id);
                      else next.add(i.id);
                      onChangeCompose({ excluded: next });
                    };
                    return (
                      <label
                        key={i.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '5px 0',
                          fontSize: 13,
                          cursor: 'pointer',
                          color: dim ? '#9ca3af' : '#333',
                        }}
                      >
                        <input type="checkbox" checked={on} onChange={toggle} />
                        <span
                          style={{
                            flex: 1,
                            textDecoration: on ? 'none' : 'line-through',
                          }}
                        >
                          {i.applicantName} — {i.jobTitle}
                        </span>
                        {skip && (
                          <span style={{ fontSize: 11, fontStyle: 'italic', color: '#9ca3af' }}>
                            {skip}
                          </span>
                        )}
                        <span style={{ flex: 1, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          expired {fmtDate(i.expiresAt)}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={onSend}
                    disabled={
                      sending ||
                      !compose.vpsoEmail ||
                      compose.people.length - compose.excluded.size === 0
                    }
                    style={{
                      background: '#1e4d3f',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor:
                        sending ||
                        !compose.vpsoEmail ||
                        compose.people.length - compose.excluded.size === 0
                          ? 'default'
                          : 'pointer',
                      opacity:
                        sending ||
                        !compose.vpsoEmail ||
                        compose.people.length - compose.excluded.size === 0
                          ? 0.7
                          : 1,
                    }}
                  >
                    {sending
                      ? 'Sending…'
                      : `Send to VPSO (${compose.people.length - compose.excluded.size})`}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={onCancelCompose}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* People rows */}
            {isOpen && (
              <div style={{ marginTop: 12 }}>
                {people.map((i) => {
                  const skip = skipLabel(i);
                  return (
                    <div
                      key={i.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '6px 0',
                        borderTop: '1px solid #f0efe9',
                        fontSize: 13,
                        color: skip ? '#9ca3af' : undefined,
                      }}
                    >
                      <span style={{ fontWeight: 500, flex: 1 }}>{i.applicantName}</span>
                      <span style={{ color: skip ? '#9ca3af' : '#666' }}>{i.jobTitle}</span>
                      {skip && (
                        <span
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            fontStyle: 'italic',
                            color: '#9ca3af',
                          }}
                        >
                          {skip}
                        </span>
                      )}
                      <span style={{ color: '#9ca3af', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {fmtDate(i.expiresAt)} ({expiredAgo(i.expiresAt)})
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function RegistrarDashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // The stats pills are refetched on their own key: an identity review resolving
  // must move the count WITHOUT remounting the section the registrar is working
  // in (that would throw away its focus, its open card and its status line).
  const [statsKey, setStatsKey] = useState(0);
  // The lodgement card's messages live HERE, outside the keyed remount below
  // (cca-omhx I4): `refresh()` remounts every section to reload it, and state
  // held inside LodgementSection died with it — a bulk mark's "N lodged;
  // failed: X" was set and discarded in the same click.
  //
  // Same lesson, two streams, two mechanisms: keep the state OUT of the keyed
  // subtree (here) or refresh WITHOUT bumping the key (`statsKey` above, and
  // RelinkControl's `load()`-not-`onRefresh()`). Neither replaces the other.
  const [lodgementError, setLodgementError] = useState<string | null>(null);
  const [packErrors, setPackErrors] = useState<Record<string, PackFailureView>>({});
  const [lodgementDownloads, setLodgementDownloads] = useState<DownloadRecord>(() => new Map());
  // Passkeys, sign-out and the lodgement-form setting live in the shared Settings
  // drawer (account/admin plumbing, off the dashboard work area).
  const settings = useSettingsDrawer();

  useEffect(() => {
    fetch('/api/vetting/registrar/lodgement-queue', { credentials: 'include' })
      .then((res) => {
        if (res.status === 401) {
          navigate(`/auth/login?returnTo=${encodeURIComponent('/registrar')}`, {
            replace: true,
          });
        } else if (res.status === 403) {
          navigate('/', { replace: true }); // Not the Registrar
        } else {
          setAuthChecked(true);
        }
      })
      .catch(() => setAuthChecked(true));
  }, []);

  if (!authChecked) {
    return (
      <AuthLayout>
        <p style={{ textAlign: 'center', color: '#666', marginTop: 48 }}>Loading…</p>
      </AuthLayout>
    );
  }

  // A full refresh bumps BOTH keys: the sections remount to reload, and the
  // stats pills (which are outside that subtree, on their own key) re-read the
  // counts that the reload may have moved.
  const refresh = () => {
    setRefreshKey((k) => k + 1);
    setStatsKey((k) => k + 1);
  };
  const lodgementMessages: LodgementMessages = {
    error: lodgementError,
    setError: setLodgementError,
    packErrors,
    applyDownloadOutcome: (key, outcome) =>
      setPackErrors((prev) => packErrorsAfter(prev, key, outcome)),
    downloads: lodgementDownloads,
    setDownloads: setLodgementDownloads,
  };

  return (
    <AuthLayout
      showSignOut
      headerExtra={
        <button
          type="button"
          className={`btn-header${settings.open ? ' is-open' : ''}`}
          aria-label="Settings"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          {...settings.buttonProps}
        >
          <GearIcon />
          Settings
        </button>
      }
    >
      <VettingSettings open={settings.open} onClose={settings.onClose} />
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          Registrar — Police Vetting Dashboard
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 32 }}>
          Lodge applications, record results, and check who held applications belong to.
        </p>

        <StatsBar refreshKey={statsKey} />
        <div key={refreshKey}>
          <div id="sec-results">
            <ResultFlagsSection />
          </div>
          <ParishLinksSection />
          <div id="sec-lodgement">
            <LodgementSection onRefresh={refresh} messages={lodgementMessages} />
          </div>
          <div id="sec-awaiting">
            <AwaitingResultSection />
          </div>
          <div id="sec-expired">
            <ExpiredSection />
          </div>
          <div id="sec-identity">
            <IdentityReviewSection onResolved={() => setStatsKey((k) => k + 1)} />
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
